/**
 * 送り先住所（基幹 JUCHU.DIRECTADDRESS）の表示用分解。
 *
 * 基幹は郵便番号・住所・電話番号を1つの自由入力欄（50文字）にまとめて入力する運用のため、
 * 表示直前に3要素へ分ける。判定は表記ゆれを吸収した文字列で行い、住所部分は原文から切り出す。
 * そのため正規化は 1文字=1文字 の置換だけに限定し、原文との添字を一致させている。
 * 郵便番号も電話番号も取り出せない場合は原文をそのまま住所として返す。
 */

/** ハイフンとして使われることがある記号（全角ハイフン・長音符・各種ダッシュ） */
const HYPHEN_LIKE = '\u2010\u2011\u2012\u2013\u2014\u2015\u2043\u02d7\u2212\uff0d\u30fc\uff70';

/** 数字に挟まれた場合のみハイフン扱いにする（地名の長音符を壊さないため） */
const RE_DIGIT_HYPHEN = new RegExp(`([0-9])[${HYPHEN_LIKE}](?=[0-9])`, 'g');

/** 先頭の郵便番号。7桁の直後が数字やハイフンなら郵便番号とみなさない */
const RE_POSTAL_HEAD = /^\u3012?\s*(\d{3})-?(\d{4})(?![-\d])/;

/** 末尾の電話番号。末尾に固定することで番地のハイフンを誤認しない */
const RE_TEL_TAIL =
  /\s*(?:\u2121|\u260e|TEL|\u96fb\u8a71)?\s*:?\s*(0\d{1,4}-\d{1,4}-\d{3,4}|0\d{9,10})\s*$/i;

/** 電話番号だけで構成された文字列（DIRECTTEL の検証用） */
const RE_TEL_EXACT = /^(0\d{1,4}-\d{1,4}-\d{3,4}|0\d{9,10})$/;

function normalizeChar(ch) {
  const code = ch.charCodeAt(0);
  if (code >= 0xff10 && code <= 0xff19) return String.fromCharCode(code - 0xff10 + 0x30);
  if (code >= 0xff21 && code <= 0xff3a) return String.fromCharCode(code - 0xff21 + 0x41);
  if (code >= 0xff41 && code <= 0xff5a) return String.fromCharCode(code - 0xff41 + 0x61);
  if (code === 0xff1a) return ':';
  if (code === 0x3000) return ' ';
  return ch;
}

/** 判定用に表記を揃える。文字数は原文と必ず一致する */
function normalizeForScan(value) {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    out += normalizeChar(value[i]);
  }
  return out.replace(RE_DIGIT_HYPHEN, '$1-');
}

/** 国内の固定電話・携帯として桁数がありえるか */
function isPlausibleTel(value) {
  const digits = value.replace(/-/g, '');
  return digits.length === 10 || digits.length === 11;
}

/**
 * 電話番号項目（基幹 JUCHU.DIRECTTEL）を表示用に検証する。
 * 電話番号として読めない値（未入力・「（明細により異なる）」等）は null を返す。
 */
export function normalizeTelValue(value) {
  if (value == null) return null;
  const scanned = normalizeForScan(String(value)).trim();
  const matched = scanned.match(RE_TEL_EXACT);
  if (!matched) return null;
  return isPlausibleTel(matched[1]) ? matched[1] : null;
}

/**
 * 送り先住所を郵便番号・住所・電話番号に分ける。
 * @param {string|null|undefined} rawAddress 基幹の送り先住所
 * @param {string|null|undefined} fallbackTel 住所欄から電話が取れないときに使う電話番号項目
 * @returns {{ postalCode: string|null, address: string, tel: string|null, raw: string, isEmpty: boolean, isSplit: boolean }}
 */
export function parseDirectAddress(rawAddress, fallbackTel) {
  const raw = (rawAddress == null ? '' : String(rawAddress)).trim();

  if (raw === '') {
    return { postalCode: null, address: '', tel: null, raw: '', isEmpty: true, isSplit: false };
  }

  const scanned = normalizeForScan(raw);
  let start = 0;
  let end = raw.length;
  let postalCode = null;
  let tel = null;

  const postalMatch = scanned.match(RE_POSTAL_HEAD);
  if (postalMatch) {
    postalCode = `${postalMatch[1]}-${postalMatch[2]}`;
    start = postalMatch[0].length;
  }

  const telMatch = scanned.slice(start).match(RE_TEL_TAIL);
  if (telMatch && isPlausibleTel(telMatch[1])) {
    tel = telMatch[1];
    end = start + telMatch.index;
  }

  // 何も取り出せなかった場合は start/end が動かないため、原文がそのまま住所になる
  return {
    postalCode,
    address: raw.slice(start, end).trim(),
    tel: tel || normalizeTelValue(fallbackTel),
    raw,
    isEmpty: false,
    isSplit: postalCode != null || tel != null,
  };
}
